/** Socket.IO client initialization. */

import { io, Socket } from 'socket.io-client';
import { apiClient } from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import { fetchDashboardData } from '@/api/services/dashboard';
import type { UptimeStatusResponse } from '@/types/uptimeStatus';
import { WS_BASE_URL } from './constants';
import { showToast } from './helpers';
import { perfMark, perfMeasure } from './perf';
import { buildOperatorStatusModalHtml, type ConnectionStatus } from './uptimeStatusUi';

let socket: Socket | null = null;
const snapshotVersions: Record<string, number> = {
  dashboard: 0,
  live_feed: 0,
  influencers: 0,
  due_diligence: 0,
};
let refreshTimer: number | null = null;
let pendingRefreshData: { count?: number; ticker?: string; timestamp?: string } | null = null;
let pendingRefreshFlowId: string | null = null;
const REFRESH_DEBOUNCE_MS = 750;
let socketFlowCounter = 0;
interface ConnectionEvent {
  status: ConnectionStatus;
  time: number;
}
const statusEvents: ConnectionEvent[] = [];
const dataUpdateTimes: number[] = [];
let currentStatus: ConnectionStatus = 'disconnected';
let serverUptimeSnapshot: UptimeStatusResponse | null = null;
let serverUptimeFetchError: string | null = null;
let serverUptimeLoading = false;
let statusDetailsInitialized = false;
let statusChartRefreshTimer: number | null = null;
const STATUS_EVENT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 day window
const MAX_EVENT_LOG = 30;
let hasConnectedOnce = false;
let visibilityRefreshHandlerBound = false;
let viewRefreshHandlers: {
  refreshDashboard?: () => Promise<void>;
  refreshLiveFeed?: () => Promise<void>;
  refreshInfluencers?: () => Promise<void>;
  refreshDueDiligence?: () => Promise<void>;
  refreshNews?: () => Promise<void>;
  refreshWatchlist?: () => Promise<void>;
  refreshDeepDive?: (ticker: string) => Promise<void>;
  refreshInfluencerDeepDive?: (author: string) => Promise<void>;
} = {};

export function setViewRefreshHandlers(handlers: typeof viewRefreshHandlers): void {
  viewRefreshHandlers = { ...viewRefreshHandlers, ...handlers };
}

function parseBackendTimestamp(timestamp: string | null | undefined): number | null {
  if (!timestamp) {
    return null;
  }
  // Backend timestamps are formatted as "YYYY-MM-DD HH:MM:SS" (UTC).
  const isoUtc = `${timestamp.replace(' ', 'T')}Z`;
  const parsed = Date.parse(isoUtc);
  return Number.isFinite(parsed) ? parsed : null;
}

export function recordBackendFreshness(
  currentTime: string | null | undefined,
  lastMentionTime: string | null | undefined
): void {
  parseBackendTimestamp(currentTime);
  parseBackendTimestamp(lastMentionTime);
  refreshStatusDetails();
}

async function fetchLatestBackendFreshness(): Promise<void> {
  try {
    const payload = await fetchDashboardData();
    if (!payload) {
      return;
    }
    recordBackendFreshness(payload.current_time, payload.last_mention_time);
  } catch (error) {
    console.warn('Failed to fetch backend freshness snapshot:', error);
  }
}

async function fetchServerUptimeSnapshot(): Promise<void> {
  try {
    const response = await apiClient.get<UptimeStatusResponse>(API_ENDPOINTS.statusUptime, 1);
    serverUptimeSnapshot = response?.data ?? null;
    serverUptimeFetchError = null;
    if (!serverUptimeSnapshot) {
      serverUptimeFetchError = 'Server returned an empty uptime payload.';
    }
  } catch (error) {
    serverUptimeSnapshot = null;
    serverUptimeFetchError =
      error instanceof Error ? error.message : 'Failed to load server uptime metrics.';
    console.warn('Failed to fetch persisted uptime snapshot:', error);
  }
}

export function initSocketIO(): void {
  try {
    initStatusDetails();
    socket = io(WS_BASE_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
    });
    bindVisibilityRefreshHandler();

    socket.on('connect', () => {
      if (import.meta.env.DEV) {
        console.log('✅ Connected to server');
      }
      const isReconnect = hasConnectedOnce;
      hasConnectedOnce = true;
      updateStatusIndicator('connected');
      if (isReconnect) {
        void refreshCurrentView({});
      }
    });

    socket.on('disconnect', (reason) => {
      if (import.meta.env.DEV) {
        console.log('❌ Disconnected from server, reason:', reason);
      }
      updateStatusIndicator('disconnected');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Connection error:', error);
      updateStatusIndicator('error');
    });

    socket.on('data_update', (data: { count?: number; ticker?: string; timestamp?: string }) => {
      const flowId = `socket.data_update.${Date.now()}.${socketFlowCounter++}`;
      perfMark(`${flowId}.received`);
      recordDataUpdate(data.timestamp);
      queueRefresh(data, flowId);
    });

    socket.on(
      'snapshot_version',
      (data: {
        dashboard?: number;
        live_feed?: number;
        influencers?: number;
        due_diligence?: number;
        deep_dive?: number;
        ticker?: string;
        timestamp?: string;
      }) => {
        let changed = false;
        if (typeof data.dashboard === 'number' && data.dashboard > (snapshotVersions.dashboard ?? 0)) {
          snapshotVersions.dashboard = data.dashboard;
          changed = true;
        }
        if (typeof data.live_feed === 'number' && data.live_feed > (snapshotVersions.live_feed ?? 0)) {
          snapshotVersions.live_feed = data.live_feed;
          changed = true;
        }
        if (
          typeof data.influencers === 'number' &&
          data.influencers > (snapshotVersions.influencers ?? 0)
        ) {
          snapshotVersions.influencers = data.influencers;
          changed = true;
        }
        if (
          typeof data.due_diligence === 'number' &&
          data.due_diligence > (snapshotVersions.due_diligence ?? 0)
        ) {
          snapshotVersions.due_diligence = data.due_diligence;
          changed = true;
        }
        if (!changed) {
          return;
        }
        const flowId = `socket.snapshot_version.${Date.now()}.${socketFlowCounter++}`;
        perfMark(`${flowId}.received`);
        queueRefresh({ ticker: data.ticker, timestamp: data.timestamp }, flowId);
      }
    );
  } catch (error) {
    console.error('❌ Error initializing Socket.IO:', error);
    showToast('Failed to connect to live feed', true);
  }
}

function bindVisibilityRefreshHandler(): void {
  if (visibilityRefreshHandlerBound || typeof document === 'undefined') {
    return;
  }
  visibilityRefreshHandlerBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void refreshCurrentView({});
    }
  });
}

function waitForNextPaint(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    let resolved = false;
    const finish = (): void => {
      if (!resolved) {
        resolved = true;
        resolve();
      }
    };

    // Guard for test environments where RAF may not fire.
    window.setTimeout(finish, 32);

    if (typeof window.requestAnimationFrame !== 'function') {
      window.setTimeout(finish, 0);
      return;
    }

    // Two RAFs to better approximate "next painted frame".
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => finish());
    });
  });
}

function queueRefresh(data: { count?: number; ticker?: string; timestamp?: string }, flowId: string): void {
  pendingRefreshData = data;
  pendingRefreshFlowId = flowId;
  if (refreshTimer) {
    window.clearTimeout(refreshTimer);
  }
  refreshTimer = window.setTimeout(() => {
    const payload = pendingRefreshData;
    const queuedFlowId = pendingRefreshFlowId;
    pendingRefreshData = null;
    pendingRefreshFlowId = null;
    refreshTimer = null;
    if (!payload || !queuedFlowId) return;

    void (async () => {
      perfMark(`${queuedFlowId}.refresh_start`);
      await refreshCurrentView(payload);
      perfMark(`${queuedFlowId}.refresh_end`);
      perfMeasure('socket.data_update_to_refresh_ms', `${queuedFlowId}.received`, `${queuedFlowId}.refresh_end`, { clear: false });
      perfMeasure('socket.refresh_handler_ms', `${queuedFlowId}.refresh_start`, `${queuedFlowId}.refresh_end`, { clear: false });
      await waitForNextPaint();
      perfMark(`${queuedFlowId}.paint_end`);
      perfMeasure('socket.data_update_to_paint_ms', `${queuedFlowId}.received`, `${queuedFlowId}.paint_end`, { clear: false });
      perfMeasure('socket.refresh_to_paint_ms', `${queuedFlowId}.refresh_end`, `${queuedFlowId}.paint_end`);
      performance.clearMarks(`${queuedFlowId}.received`);
      performance.clearMarks(`${queuedFlowId}.refresh_start`);
      performance.clearMeasures('socket.data_update_to_refresh_ms');
      performance.clearMeasures('socket.refresh_handler_ms');
      performance.clearMeasures('socket.data_update_to_paint_ms');
    })();
  }, REFRESH_DEBOUNCE_MS);
}

export function getSocket(): Socket | null {
  return socket;
}

function updateStatusIndicator(status: 'connected' | 'disconnected' | 'error'): void {
  const statusLight = document.getElementById('status-light');
  const statusText = document.getElementById('status-text');
  currentStatus = status;
  recordStatusEvent(status);
  
  if (statusLight && statusText) {
    if (status === 'connected') {
      statusLight.className = 'w-3 h-3 bg-green-500 rounded-full';
      statusText.textContent = 'Connected';
    } else if (status === 'disconnected') {
      statusLight.className = 'w-3 h-3 bg-red-500 rounded-full animate-pulse';
      statusText.textContent = 'Disconnected';
    } else {
      statusLight.className = 'w-3 h-3 bg-yellow-500 rounded-full animate-pulse';
      statusText.textContent = 'Connection error';
    }
  }
  refreshStatusDetails();
}

function recordStatusEvent(status: ConnectionStatus): void {
  const now = Date.now();
  statusEvents.push({ status, time: now });
  trimOldStatusData(now);
}

function recordDataUpdate(timestamp?: string): void {
  const parsed = timestamp ? Date.parse(timestamp) : NaN;
  const time = Number.isFinite(parsed) ? parsed : Date.now();
  dataUpdateTimes.push(time);
  trimOldStatusData(Date.now());
  refreshStatusDetails();
}

function trimOldStatusData(now: number): void {
  const cutoff = now - STATUS_EVENT_WINDOW_MS;
  while (statusEvents.length > 1 && statusEvents[0].time < cutoff) {
    statusEvents.shift();
  }
  while (dataUpdateTimes.length && dataUpdateTimes[0] < cutoff) {
    dataUpdateTimes.shift();
  }
}

function initStatusDetails(): void {
  if (statusDetailsInitialized) {
    return;
  }
  statusDetailsInitialized = true;

  const statusIndicator = document.getElementById('status-indicator');
  const modal = document.getElementById('status-detail-modal');
  const closeBtn = document.getElementById('status-detail-close-btn');

  if (!statusIndicator || !modal || !closeBtn) {
    return;
  }

  statusIndicator.addEventListener('click', async () => {
    serverUptimeLoading = true;
    serverUptimeFetchError = null;
    refreshStatusDetails();
    modal.classList.remove('hidden');
    statusIndicator.setAttribute('aria-expanded', 'true');
    startStatusChartRefresh();
    await Promise.all([fetchLatestBackendFreshness(), fetchServerUptimeSnapshot()]);
    serverUptimeLoading = false;
    refreshStatusDetails();
  });

  closeBtn.addEventListener('click', () => {
    closeStatusDetails();
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      closeStatusDetails();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.classList.contains('hidden')) {
      closeStatusDetails();
    }
  });
}

function closeStatusDetails(): void {
  const statusIndicator = document.getElementById('status-indicator');
  const modal = document.getElementById('status-detail-modal');
  if (!modal) {
    return;
  }
  modal.classList.add('hidden');
  statusIndicator?.setAttribute('aria-expanded', 'false');
  stopStatusChartRefresh();
}

function startStatusChartRefresh(): void {
  if (statusChartRefreshTimer) {
    return;
  }
  statusChartRefreshTimer = window.setInterval(() => {
    void (async () => {
      await Promise.all([fetchLatestBackendFreshness(), fetchServerUptimeSnapshot()]);
      refreshStatusDetails();
    })();
  }, 30000);
}

function stopStatusChartRefresh(): void {
  if (!statusChartRefreshTimer) {
    return;
  }
  window.clearInterval(statusChartRefreshTimer);
  statusChartRefreshTimer = null;
}

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildRecentEventsTableBody(): string {
  const recentEvents = [...statusEvents].slice(-MAX_EVENT_LOG).reverse();
  if (!recentEvents.length) {
    return `<tr><td colspan="2">No events yet</td></tr>`;
  }
  return recentEvents
    .map(
      (event) => `
            <tr>
              <td>${new Date(event.time).toLocaleTimeString()}</td>
              <td>${labelFromStatus(event.status)}</td>
            </tr>`
    )
    .join('');
}

function refreshStatusDetails(): void {
  const container = document.getElementById('status-detail-content');
  if (!container) {
    return;
  }
  const modal = document.getElementById('status-detail-modal');
  const modalOpen = modal !== null && !modal.classList.contains('hidden');

  if (serverUptimeLoading && modalOpen) {
    container.innerHTML = `<div class="status-detail-loading text-secondary py-8 text-center">Loading server health…</div>`;
    return;
  }
  if (serverUptimeFetchError && modalOpen) {
    container.innerHTML = `
    <div class="status-uptime-error card p-4 border border-red-500/40 bg-red-950/20 rounded-md">
      <p class="text-red-200 font-semibold mb-2">Could not load server health metrics</p>
      <p class="text-secondary text-sm mb-4">${escapeHtml(serverUptimeFetchError)}</p>
      <button type="button" id="status-uptime-retry" class="px-3 py-1 bg-neon-cyan text-black rounded-md text-sm font-semibold">
        Retry
      </button>
    </div>`;
    const retry = container.querySelector('#status-uptime-retry');
    retry?.addEventListener('click', () => {
      void (async () => {
        serverUptimeLoading = true;
        serverUptimeFetchError = null;
        refreshStatusDetails();
        await Promise.all([fetchLatestBackendFreshness(), fetchServerUptimeSnapshot()]);
        serverUptimeLoading = false;
        refreshStatusDetails();
      })();
    });
    return;
  }
  if (serverUptimeSnapshot) {
    renderServerStatusDetails(container, serverUptimeSnapshot);
    return;
  }
  container.innerHTML = `
    <p class="text-secondary text-sm">
      Open <strong>Data Uptime &amp; Freshness</strong> from the header to load server-wide metrics. Browser-only heatmaps are no longer used as a fallback.
    </p>
  `;
}

function renderServerStatusDetails(container: HTMLElement, snapshot: UptimeStatusResponse): void {
  const now = Date.now();
  const lastTabDataUpdateMsAgo = dataUpdateTimes.length
    ? now - dataUpdateTimes[dataUpdateTimes.length - 1]
    : null;
  const avgUpdateSeconds = getAverageUpdateIntervalSeconds();
  const avgTabUpdateIntervalLabel =
    avgUpdateSeconds !== null ? `${avgUpdateSeconds.toFixed(1)}s` : 'Need at least 2 updates in this tab';
  const recentEventsHtml = buildRecentEventsTableBody();
  container.innerHTML = buildOperatorStatusModalHtml(snapshot, {
    currentStatus,
    lastTabDataUpdateMsAgo,
    avgTabUpdateIntervalLabel,
    recentEventsHtml,
  });
}

function getAverageUpdateIntervalSeconds(): number | null {
  if (dataUpdateTimes.length < 2) {
    return null;
  }
  let totalDiff = 0;
  for (let i = 1; i < dataUpdateTimes.length; i += 1) {
    totalDiff += dataUpdateTimes[i] - dataUpdateTimes[i - 1];
  }
  return totalDiff / (dataUpdateTimes.length - 1) / 1000;
}

function labelFromStatus(status: ConnectionStatus): string {
  if (status === 'connected') {
    return 'Connected';
  }
  if (status === 'disconnected') {
    return 'Disconnected';
  }
  return 'Connection error';
}

/**
 * Refresh the current view when data updates are received.
 */
async function refreshCurrentView(_data: { count?: number; ticker?: string; timestamp?: string }): Promise<void> {
  try {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    // Get current view from navigation store
    const { useNavigationStore } = await import('@/stores/navigation');
    const { currentView, viewParams } = useNavigationStore.getState();
    
    // Refresh based on current view.
    // live_feed refresh keeps slice/scroll via view options.
    switch (currentView) {
      case 'dashboard':
        if (viewRefreshHandlers.refreshDashboard) {
          await viewRefreshHandlers.refreshDashboard();
        }
        break;
      case 'live_feed':
        if (viewRefreshHandlers.refreshLiveFeed) {
          await viewRefreshHandlers.refreshLiveFeed();
        }
        break;
      case 'top_influencers':
        if (viewRefreshHandlers.refreshInfluencers) {
          await viewRefreshHandlers.refreshInfluencers();
        }
        break;
      case 'due_diligence':
        if (viewRefreshHandlers.refreshDueDiligence) {
          await viewRefreshHandlers.refreshDueDiligence();
        }
        break;
      case 'news':
        if (viewRefreshHandlers.refreshNews) {
          await viewRefreshHandlers.refreshNews();
        }
        break;
      case 'watchlist':
        if (viewRefreshHandlers.refreshWatchlist) {
          await viewRefreshHandlers.refreshWatchlist();
        }
        break;
      case 'deep_dive':
        if (viewParams?.ticker && viewRefreshHandlers.refreshDeepDive) {
          await viewRefreshHandlers.refreshDeepDive(viewParams.ticker);
        }
        break;
      case 'influencer_deep_dive':
        if (viewParams?.author && viewRefreshHandlers.refreshInfluencerDeepDive) {
          await viewRefreshHandlers.refreshInfluencerDeepDive(viewParams.author);
        }
        break;
      default:
        // No refresh needed for this view
        break;
    }
  } catch (error) {
    console.error('Error refreshing current view:', error);
  }
}

